export const DEFAULT_MAX_SUGGESTION_DISTANCE_KM = 8;
const DEFAULT_MIN_GAP_KM = 1;

function toNumber(value) {
  if (value === null || typeof value === "undefined" || value === "") {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
}

function isDisabled(item) {
  const value = item?.pivot?.disabled;

  return value === true || value === 1 || value === "1" || value === "true";
}

function groupKey(item) {
  return `${item.zcCountryId}|${item.zcZip}`;
}

function normalizeBounds(bounds) {
  if (!bounds) {
    return null;
  }

  const north = toNumber(bounds.north);
  const south = toNumber(bounds.south);
  const east = toNumber(bounds.east);
  const west = toNumber(bounds.west);

  if ([north, south, east, west].some((value) => value === null)) {
    return null;
  }

  return {
    north: Math.max(north, south),
    south: Math.min(north, south),
    east: Math.max(east, west),
    west: Math.min(east, west),
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function hasValidCenter(item) {
  return toNumber(item?.zcLat) !== null && toNumber(item?.zcLng) !== null;
}

function hasValidLocation(item) {
  return hasValidCenter(item) || normalizeBounds(item?.bounds) !== null;
}

function haversineDistanceKm(origin, destination) {
  const earthRadiusKm = 6371;
  const originLat = origin.lat * (Math.PI / 180);
  const destinationLat = destination.lat * (Math.PI / 180);
  const latDiff = destinationLat - originLat;
  const lngDiff = (destination.lng - origin.lng) * (Math.PI / 180);

  return 2 * earthRadiusKm * Math.asin(Math.sqrt(
    Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
      Math.cos(originLat) *
        Math.cos(destinationLat) *
        Math.sin(lngDiff / 2) *
        Math.sin(lngDiff / 2)
  ));
}

function boundsIntersectCircle(bounds, center, radiusKm) {
  const normalizedBounds = normalizeBounds(bounds);

  if (!normalizedBounds) {
    return false;
  }

  const nearestPoint = {
    lat: clamp(center.lat, normalizedBounds.south, normalizedBounds.north),
    lng: clamp(center.lng, normalizedBounds.west, normalizedBounds.east),
  };

  return haversineDistanceKm(center, nearestPoint) <= radiusKm;
}

function isWithinSuggestionCircle(item, restaurantLocation, radiusKm) {
  if (radiusKm === null) {
    return true;
  }

  return item.distance <= radiusKm ||
    boundsIntersectCircle(item.bounds, restaurantLocation, radiusKm);
}

function withDistance(item, restaurantLocation) {
  const bounds = normalizeBounds(item.bounds);
  const center = hasValidCenter(item)
    ? {
      lat: toNumber(item.zcLat),
      lng: toNumber(item.zcLng),
    }
    : {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2,
    };

  return {
    id: item.id,
    zcZip: item.zcZip,
    zcCity: item.zcCity,
    zcLat: item.zcLat,
    zcLng: item.zcLng,
    zcCountryId: item.zcCountryId,
    bounds: item.bounds,
    distance: haversineDistanceKm(restaurantLocation, center),
    disabled: isDisabled(item),
  };
}

function pickNearestByPostcode(groups, item) {
  const key = groupKey(item);
  const current = groups.get(key);

  if (!current || item.distance < current.distance) {
    groups.set(key, item);
  }
}

function pickFarthestByPostcode(groups, item) {
  const key = groupKey(item);
  const current = groups.get(key);

  if (!current || item.distance > current.distance) {
    groups.set(key, item);
  }
}

export function getDeliveryZoneGapWarnings({
  linkedZipCodes = [],
  allZipCodes = [],
  restaurantLat = null,
  restaurantLng = null,
  restaurantCountry = null,
  minGapKm = DEFAULT_MIN_GAP_KM,
  maxSuggestionDistanceKm = DEFAULT_MAX_SUGGESTION_DISTANCE_KM,
  useDeliveryCircleSuggestions = false,
} = {}) {
  const maximumSuggestionDistanceKm = toNumber(maxSuggestionDistanceKm);
  const restaurantLocation = {
    lat: toNumber(restaurantLat),
    lng: toNumber(restaurantLng),
  };

  if (restaurantLocation.lat === null || restaurantLocation.lng === null) {
    return {
      farthestActiveZone: null,
      suggestedZones: [],
      minGapKm,
      maxSuggestionDistanceKm: maximumSuggestionDistanceKm,
      useDeliveryCircleSuggestions,
    };
  }

  const country = Number(restaurantCountry);
  const activeGroups = new Map();
  const activeKeys = new Set();

  linkedZipCodes
    .filter((item) => Number(item.zcCountryId) === country)
    .filter((item) => !isDisabled(item))
    .filter(hasValidLocation)
    .map((item) => withDistance(item, restaurantLocation))
    .forEach((item) => {
      activeKeys.add(groupKey(item));
      pickFarthestByPostcode(activeGroups, item);
    });

  const activeZones = Array.from(activeGroups.values());
  const farthestActiveZone = activeZones.length
    ? activeZones.reduce((farthest, item) => (
      item.distance > farthest.distance ? item : farthest
    ))
    : null;

  if (!useDeliveryCircleSuggestions && !farthestActiveZone) {
    return {
      farthestActiveZone: null,
      suggestedZones: [],
      minGapKm,
      maxSuggestionDistanceKm: maximumSuggestionDistanceKm,
      useDeliveryCircleSuggestions,
    };
  }

  const maximumSuggestedDistance = useDeliveryCircleSuggestions
    ? null
    : farthestActiveZone.distance - minGapKm;
  const suggestionDistanceCeilingKm = useDeliveryCircleSuggestions
    ? maximumSuggestionDistanceKm
    : (
      maximumSuggestionDistanceKm === null
        ? maximumSuggestedDistance
        : Math.min(maximumSuggestedDistance, maximumSuggestionDistanceKm)
    );

  if (suggestionDistanceCeilingKm !== null && suggestionDistanceCeilingKm <= 0) {
    return {
      farthestActiveZone,
      suggestedZones: [],
      minGapKm,
      maxSuggestionDistanceKm: maximumSuggestionDistanceKm,
      suggestionDistanceCeilingKm,
      useDeliveryCircleSuggestions,
    };
  }

  const candidateGroups = new Map();
  allZipCodes
    .filter((item) => Number(item.zcCountryId) === country)
    .filter(hasValidLocation)
    .map((item) => withDistance(item, restaurantLocation))
    .filter((item) => !activeKeys.has(groupKey(item)))
    .filter((item) => (
      useDeliveryCircleSuggestions
        ? isWithinSuggestionCircle(item, restaurantLocation, suggestionDistanceCeilingKm)
        : item.distance <= suggestionDistanceCeilingKm
    ))
    .forEach((item) => pickNearestByPostcode(candidateGroups, item));

  return {
    farthestActiveZone,
    suggestedZones: Array.from(candidateGroups.values())
      .sort((a, b) => a.distance - b.distance),
    minGapKm,
    maxSuggestionDistanceKm: maximumSuggestionDistanceKm,
    suggestionDistanceCeilingKm,
    useDeliveryCircleSuggestions,
  };
}
